import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  CmsReportData,
  createUserFeedbackApi,
  getCmsReportApi,
  getUserDetailsApi,
  getUserFeedbackApi,
  getUserList,
  User,
  UserFeedback,
  WeeklyUserReport,
} from "../../apis/userApis";
import { toast } from "react-toastify";

interface UserListState {
  users: User[];
  totalCount: number;
  activeCount: string;
  inactiveCount: string;
  deletedCount: string;

  loading: boolean;
  error: string | null;
  refetchKey: number;

  weeklyReport: WeeklyUserReport[] | null;
  weeklyReportLoading: boolean;

  feedbackList: UserFeedback[];
  feedbackCount: number;
  feedbackLoading: boolean;
  feedbackCreateLoading: boolean;

  cmsReport: CmsReportData | null;
  cmsReportLoading: boolean;
}

const initialState: UserListState = {
  users: [],
  totalCount: 0,
  activeCount: "0",
  inactiveCount: "0",
  deletedCount: "0",
  loading: false,
  error: null,
  refetchKey: 0,

  weeklyReport: null,
  weeklyReportLoading: false,

  feedbackList: [],
  feedbackCount: 0,
  feedbackLoading: false,
  feedbackCreateLoading: false,

  cmsReport: null,
  cmsReportLoading: false,
};

/* ----------------------------------
   Async thunk
----------------------------------- */
export const fetchUserList = createAsyncThunk(
  "users/fetchUserList",
  async ({
    pageId,
    limit,
    status,
  }: {
    pageId: number;
    limit: number;
    status: string;
  }) => {
    const response = await getUserList({
      pageId,
      limit,
      status: status as "active" | "inactive" | "delete",
    });

    return response.data;
  },
);

export const fetchUserDetails = createAsyncThunk(
  "users/fetchUserDetails",
  async (
    {
      userId,
      weekId,
      startDate,
      endDate,
    }: {
      userId: string;
      weekId?: string;
      startDate?: string;
      endDate?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const res = await getUserDetailsApi({
        userId,
        weekId,
        startDate,
        endDate,
      });

      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.data || "Failed to fetch user details",
      );
    }
  },
);

export const fetchUserFeedback = createAsyncThunk(
  "users/fetchUserFeedback",
  async (
    {
      userId,
      pageId,
      limit,
    }: { userId: string; pageId: number; limit: number },
    { rejectWithValue },
  ) => {
    try {
      const res = await getUserFeedbackApi(userId, pageId, limit);
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.data || "Failed to fetch user feedback",
      );
    }
  },
);

export const createUserFeedback = createAsyncThunk(
  "users/createUserFeedback",
  async (
    { userId, feedback }: { userId: string; feedback: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await createUserFeedbackApi(userId, { feedback });
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.data || "Failed to create feedback",
      );
    }
  },
);

export const fetchCmsReport = createAsyncThunk(
  "users/fetchCmsReport",
  async (
    { weekReportMappingId }: { weekReportMappingId: string },
    { rejectWithValue },
  ) => {
    try {
      const res = await getCmsReportApi(weekReportMappingId);
      return res.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.data || "Failed to fetch CMS report",
      );
    }
  },
);

/* ----------------------------------
   Slice
----------------------------------- */
const userListSlice = createSlice({
  name: "userList",
  initialState,
  reducers: {
    refetchUserList: (state) => {
      state.refetchKey += 1; // 🔥 trigger useEffect dependency
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserList.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder
      .addCase(fetchUserList.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload.finalData;
        state.totalCount = action.payload.count;
        state.activeCount = action.payload.activeCount;
        state.inactiveCount = action.payload.inactiveCount;
        state.deletedCount = action.payload.deletedCount;
      })
      .addCase(fetchUserList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to fetch users");
      })

      .addCase(fetchUserDetails.pending, (state) => {
        state.weeklyReportLoading = true;
        state.error = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.weeklyReportLoading = false;
        state.weeklyReport = action.payload;
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.weeklyReportLoading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchUserFeedback.pending, (state) => {
        state.feedbackLoading = true;
        state.error = null;
      })
      .addCase(fetchUserFeedback.fulfilled, (state, action) => {
        state.feedbackLoading = false;
        state.feedbackList = action.payload.rows;
        state.feedbackCount = action.payload.count;
      })
      .addCase(fetchUserFeedback.rejected, (state, action) => {
        state.feedbackLoading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to fetch feedback");
      })

      /* =============================
   CREATE FEEDBACK
============================= */

      .addCase(createUserFeedback.pending, (state) => {
        state.feedbackCreateLoading = true;
        state.error = null;
      })
      .addCase(createUserFeedback.fulfilled, (state, action) => {
        state.feedbackCreateLoading = false;

        // 🔥 Optimistic Update (prepend latest feedback)
        state.feedbackList = [
          {
            ...action.payload,
            creator: null,
            updater: null,
          },
          ...state.feedbackList,
        ];
        toast.success("Feedback created successfully");
      })
      .addCase(createUserFeedback.rejected, (state, action) => {
        state.feedbackCreateLoading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to create feedback");
      })

      /* =============================
   FETCH CMS REPORT
============================= */

      .addCase(fetchCmsReport.pending, (state) => {
        state.cmsReportLoading = true;
        state.error = null;
      })
      .addCase(fetchCmsReport.fulfilled, (state, action) => {
        state.cmsReportLoading = false;
        state.cmsReport = action.payload;
      })
      .addCase(fetchCmsReport.rejected, (state, action) => {
        state.cmsReportLoading = false;
        state.error = action.payload as string;
        toast.error(state.error || "Failed to fetch user details");
      });
  },
});

export const { refetchUserList } = userListSlice.actions;

export default userListSlice.reducer;
